import React, { useEffect, useMemo, useRef, useState } from 'react';
import pb from '../lib/pocketbase';

import { useHeroSlidesData } from '../context/HeroSlidesContext';

export default function HeroSlidesPage() {
  const { slides: contextSlides, loading: contextLoading, refresh } = useHeroSlidesData();
  const [loading, setLoading] = useState(false); // Local loading for actions
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [filterPublished, setFilterPublished] = useState('all'); // all|published|unpublished
  const dragIndex = useRef(-1);

  // Local items state for filtering/sorting/dragging, initialized from context
  const [items, setItems] = useState(contextSlides || []);

  useEffect(() => {
    if (contextSlides) {
      setItems(contextSlides);
    }
  }, [contextSlides]);


  // We rely on context for initial load. 
  // Local filtering is done on client side or we can request specific filter from context if we broaden context capabilities.
  // For now, let's filter client-side since list is small.
  const filteredItems = useMemo(() => {
    if (filterPublished === 'all') return items;
    return items.filter(i => filterPublished === 'published' ? i.published : !i.published);
  }, [items, filterPublished]);

  const load = async () => {
    await refresh();
  };


  // Compute next order from current items
  const nextOrderBase = useMemo(() => {
    if (!items || items.length === 0) return 0;
    const max = items.reduce((m, r) => (typeof r.order === 'number' ? Math.max(m, r.order) : m), 0);
    return Math.max(max, items.length);
  }, [items]);

  const handleFilesUpload = async (fileList) => {
    const files = Array.from(fileList || []).filter(Boolean);
    if (!files.length) return;
    setUploading(true);
    setError('');
    try {
      let ord = nextOrderBase;
      for (const f of files) {
        ord += 1;
        const processed = await compressImageFile(f);
        const fd = new FormData();
        fd.append('image', processed.blob, processed.filename);
        fd.append('order', String(ord));
        fd.append('published', 'true');
        const baseName = (f.name || '').replace(/\.[^.]+$/, '');
        if (baseName) fd.append('alt', baseName);
        await pb.collection('hero_slides').create(fd);
      }
      await load();
    } catch (e) {
      const msg = String(e?.message || '');
      if (/auto-?cancel/i.test(msg)) return;
      setError(msg || "Échec de l'upload");
    } finally {
      setUploading(false);
    }
  };


  const fileUrl = (rec) => (rec?.image ? pb.files.getURL(rec, rec.image) : null);

  // ---- Utils: client-side image compression to avoid large uploads (413) ----
  async function compressImageFile(file) {
    // If the file is already relatively small (< 1.2MB), upload as-is
    const SMALL_SIZE = 1.2 * 1024 * 1024;
    if (file.size <= SMALL_SIZE) {
      return { blob: file, filename: file.name };
    }

    const bitmap = await createImageBitmap(file).catch(async () => {
      // Fallback via HTMLImageElement if createImageBitmap fails
      const img = await loadImageFromFile(file);
      return await createImageBitmap(img);
    });
    const maxW = 1920;
    const maxH = 1080;
    const { width: srcW, height: srcH } = bitmap;
    let dstW = srcW;
    let dstH = srcH;
    const ratio = Math.min(maxW / srcW, maxH / srcH, 1);
    dstW = Math.round(srcW * ratio);
    dstH = Math.round(srcH * ratio);

    const canvas = document.createElement('canvas');
    canvas.width = dstW;
    canvas.height = dstH;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(bitmap, 0, 0, dstW, dstH);

    // Try WebP first (smaller), fallback to JPEG
    const QUALITY = 0.82;
    const webpBlob = await canvasToBlob(canvas, 'image/webp', QUALITY);
    if (webpBlob && webpBlob.size < file.size) {
      return { blob: webpBlob, filename: renameExt(file.name, 'webp') };
    }
    const jpegBlob = await canvasToBlob(canvas, 'image/jpeg', QUALITY);
    if (jpegBlob && jpegBlob.size < file.size) {
      return { blob: jpegBlob, filename: renameExt(file.name, 'jpg') };
    }
    // Fallback: return original file if compression didn’t help
    return { blob: file, filename: file.name };
  }

  function canvasToBlob(canvas, type, quality) {
    return new Promise((resolve) => canvas.toBlob(resolve, type, quality));
  }

  function loadImageFromFile(file) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = URL.createObjectURL(file);
    });
  }

  function renameExt(filename, newExt) {
    const base = filename.replace(/\.[^.]+$/, '');
    return `${base}.${newExt}`;
  }

  const onDragStart = (e, index) => {
    dragIndex.current = index;
    e.dataTransfer.effectAllowed = 'move';
  };

  const onDragOver = (e, overIndex) => {
    e.preventDefault();
    const from = dragIndex.current;
    if (from === -1 || from === overIndex) return;
    setItems((arr) => {
      const next = arr.slice();
      const [moved] = next.splice(from, 1);
      next.splice(overIndex, 0, moved);
      dragIndex.current = overIndex;
      return next;
    });
  };

  const onDrop = async () => {
    dragIndex.current = -1;
    await persistOrder();
  };

  const persistOrder = async () => {
    setSaving(true);
    setError('');
    try {
      // compute new order values (1-based indexes)
      const ops = items.map((rec, idx) => pb.collection('hero_slides').update(rec.id, { order: idx + 1 }));
      await Promise.all(ops);
      refresh(); // Update context
    } catch (e) {
      setError(String(e?.message || 'Erreur lors de la sauvegarde'));
    } finally {
      setSaving(false);
    }
  };

  const togglePublished = async (rec) => {
    try {
      const updated = await pb.collection('hero_slides').update(rec.id, { published: !rec.published });
      setItems((arr) => arr.map((it) => (it.id === rec.id ? updated : it)));
    } catch (e) {
      setError(String(e?.message || 'Impossible de mettre à jour published'));
    }
  };

  const removeSlide = async (rec) => {
    if (!rec?.id) return;
    const ok = window.confirm('Supprimer ce slide ? Cette action est irréversible.');
    if (!ok) return;
    try {
      await pb.collection('hero_slides').delete(rec.id);
      await load();
    } catch (e) {
      const msg = String(e?.message || '');
      if (/auto-?cancel/i.test(msg)) return;
      setError(msg || 'Suppression impossible');
    }
  };

  return (
    <section className="section">
      <div className="section-inner">
        <div className="section-header">
          <h2>Hero slides</h2>
          <div className="admin-users-controls" style={{ gap: '0.5rem' }}>
            <select value={filterPublished} onChange={(e) => setFilterPublished(e.target.value)}>
              <option value="all">Tous</option>
              <option value="published">Publiés</option>
              <option value="unpublished">Non publiés</option>
            </select>
            <button type="button" className="btn" onClick={load} disabled={loading}>{loading ? 'Chargement…' : 'Rafraîchir'}</button>
            <button type="button" className="btn btn-primary" onClick={persistOrder} disabled={saving || items.length === 0}>{saving ? 'Enregistrement…' : 'Enregistrer l\'ordre'}</button>
          </div>
        </div>

        <div
          onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; }}
          onDrop={(e) => { e.preventDefault(); handleFilesUpload(e.dataTransfer.files); }}
          style={{
            border: '1px dashed var(--border-subtle)',
            borderRadius: '10px',
            padding: '16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '1rem',
            marginBottom: '1rem',
            background: 'var(--bg-subtle)'
          }}
          aria-label="Zone de dépose pour importer des slides"
        >
          <div style={{ color: 'var(--text-muted)' }}>
            Glissez-déposez vos images ici pour ajouter des slides (ou)
          </div>
          <label className="btn">
            Sélectionner des images
            <input type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={(e) => handleFilesUpload(e.target.files)} />
          </label>
          {uploading ? <span style={{ color: 'var(--text-muted)' }}>Upload en cours…</span> : null}
        </div>

        {error ? <div className="alert error" style={{ marginBottom: '1rem' }}>{error}</div> : null}

        <div className="admin-kanban" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '12px' }}>
          {filteredItems.map((rec, idx) => (
            <div
              key={rec.id}
              className="card"
              draggable
              onDragStart={(e) => onDragStart(e, idx)}
              onDragOver={(e) => onDragOver(e, idx)}
              onDrop={onDrop}
              style={{ cursor: 'grab' }}
              title={`Glissez pour réordonner (position ${idx + 1})`}
            >
              <div style={{ aspectRatio: '16/9', border: '1px solid var(--border-subtle)', borderRadius: '8px', overflow: 'hidden', background: 'var(--bg-subtle)', display: 'grid', placeItems: 'center' }}>
                {fileUrl(rec) ? (
                  <img src={fileUrl(rec)} alt={rec.alt || ''} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                  <div style={{ color: 'var(--text-muted)' }}>Pas d\'image</div>
                )}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '0.5rem' }}>
                <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>#{idx + 1} • {rec.order ?? '—'}</div>
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                  <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '0.85rem' }}>
                    Publié
                    <input type="checkbox" checked={!!rec.published} onChange={() => togglePublished(rec)} />
                  </label>
                  <button type="button" className="btn btn-ghost" onClick={() => removeSlide(rec)} disabled={saving || uploading} title="Supprimer">Supprimer</button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {filteredItems.length === 0 && !loading ? (
          <div className="admin-table-empty">Aucun slide pour le moment.</div>
        ) : null}
      </div>
    </section>
  );
}
